import React, { useState, useEffect } from "react";
import moment from "moment-timezone";
import {
  Form,
  Button,
  TextArea,
  TextInput,
  Select,
  SelectItem,
  DatePicker,
  DatePickerInput,
} from "carbon-components-react";

import { getFileDownloadBaseURL } from "config";

import { enumKeys } from "@eave/shared/utils";
import { formatCurrency } from "@eave/shared/utils/currency";
import {
  InvestorDetail,
  InvestorNames,
  WarehouseLine,
  MaxExSubInvestors,
  MaxExSubInvestorNameMap,
} from "@eave/shared/types/api";

import APIClient from "web_modules/APIClient";
import HurdleView from "underwriter_dashboard/shared/HurdleView";
import { HurdleProps } from "underwriter_dashboard/HurdleEditor";
import { logException } from "web_modules/utils/sentry";

const styles = require("./styles.module.scss");

const WarehouseLineOptions = [
  {
    value: "BankUnited",
    label: WarehouseLine.BankUnited,
  },
  {
    value: "Flagstar",
    label: WarehouseLine.Flagstar,
  },
  {
    value: "WesternAlliance",
    label: WarehouseLine.WesternAlliance,
  },
];

const dateOrNull = (value: string): string | null => {
  if (!value) {
    return null;
  }
  const date = moment.utc(new Date(value)).format();
  if (date === "Invalid date") {
    return null;
  }

  return date;
};

const decimalStringOrNull = (value: string): string | null => {
  if (!value || value.trim() === "") {
    return null;
  }
  return value;
};

const normalizeNullDates = (d: string) => {
  if (!d) {
    return null;
  }
  const [year, month, day] = d.split("-");
  const date = new Date(+year, +month - 1, +day);

  return moment(date).format("MM-DD-YYYY");
};

const convertInvestorDataToForm = investorData => {
  if (!investorData) {
    alert("Form data was not loaded ");
  }

  return {
    investorName: investorData.investor_name || "",
    subInvestor: investorData.sub_investor || "",
    warehouseLine: investorData.warehouse_line || "",
    investorLoanNumber:
      investorData.investor_loan_number === null ? "" : investorData.investor_loan_number,
    investorProgramDescription: investorData.investor_program_description || undefined,
    investorRateLockDate: normalizeNullDates(investorData.investor_rate_lock_date),
    investorRateLockExpirationDate: normalizeNullDates(
      investorData.investor_rate_lock_expiration_date,
    ),
    investorPurchaseDate: normalizeNullDates(investorData.investor_purchase_date),
    loanServiceTransferDate: normalizeNullDates(investorData.loan_service_transfer_date),

    // revenue
    expectedGain: investorData.expected_gain || "",
    actualGain: investorData.actual_gain || "",
  };
};

const DEFAULT_INVESTOR_DATA = {
  investor_name: null,
  warehouse_line: null,
  investor_loan_number: null,
  investor_program_description: null,
  investor_rate_lock_date: null,
  investor_rate_lock_expiration_date: null,
  investor_purchase_date: null,
  loan_service_transfer_date: null,
  expected_gain: "",
  actual_gain: "",
};

const getWarehouseLineButton = (appID: number, line: WarehouseLine): React.ReactNode => {
  if (line === WarehouseLine.BankUnited) {
    return (
      <Button
        kind="primary"
        onClick={() => {
          APIClient.get(`/uw/${appID}/tapes/bank_united_warehouse_funding`)
            .then((response: { s3_key: string; filename: string }) => {
              const { filename, s3_key } = response;
              window.location.href = getFileDownloadBaseURL(s3_key, filename);
            })
            .catch(err => {
              logException(err);
              alert(
                `There was an error downloading the BankUnited data tape. Please try again or notify #engineering.`,
              );
            });
        }}
      >
        Download BankUnited data tape
      </Button>
    );
  }

  if (line === WarehouseLine.Flagstar) {
    return (
      <Button kind="primary" disabled>
        Download Flagstar data tape
      </Button>
    );
  }

  return (
    <Button kind="primary" disabled>
      Select a warehouse line
    </Button>
  );
};

const InvestorRateLockHurdle = (props: HurdleProps) => {
  const { app, hurdle, onHide } = props;
  const { loan_amount } = app.computed_properties;
  const loanAmount = parseInt(loan_amount, 10);

  const [loadedInvestorData, setLoadedInvestorData] = useState<InvestorDetail>();
  const [form, setForm] = useState<any>();
  const [mers, setMers] = useState("");

  useEffect(() => {
    APIClient.get(`/uw/loan_applications/${app.id}/investor_detail`)
      .then(data => {
        const { investor_detail } = data as { investor_detail: InvestorDetail };
        setLoadedInvestorData(investor_detail);
        const formData = convertInvestorDataToForm(investor_detail);
        setForm(formData);
      })
      .catch(e => {
        const { meta } = e;
        // @ts-ignore
        const { code } = meta || {};

        // resource not found / not created yet:
        if (code && code === 404) {
          setLoadedInvestorData(DEFAULT_INVESTOR_DATA as InvestorDetail);
          const formData = convertInvestorDataToForm(DEFAULT_INVESTOR_DATA);
          setForm(formData);
        } else {
          throw new Error(e);
        }
      });

    APIClient.get(`/uw/loan_applications/${app.id}/mers`).then((data: { mers: string }) => {
      setMers(data.mers);
    });
  }, [app]);

  const handleInputChange = e => {
    const {
      target: { name, value },
    } = e;

    // Makes sure 'subInvestor' values are correctly removed if we
    // switch investors from MaxEx to another investor.
    if (name === "investorName" && value !== InvestorNames.MaxEx) {
      form.subInvestor = "";
    }

    setForm({
      ...form,
      [name]: value,
    });
  };

  const resetForm = () => {
    const formData = convertInvestorDataToForm(loadedInvestorData);
    setForm(formData);
  };

  const submitRevenueForm = e => {
    e.preventDefault();

    const formData = {
      investor_name: form.investorName === "" ? null : form.investorName,
      sub_investor: form.subInvestor === "" ? null : form.subInvestor,
      warehouse_line: form.warehouseLine,
      investor_loan_number: form.investorLoanNumber.trim() === "" ? null : form.investorLoanNumber,
      investor_rate_lock_date: dateOrNull(form.investorRateLockDate),
      investor_rate_lock_expiration_date: dateOrNull(form.investorRateLockExpirationDate),
      investor_purchase_date: dateOrNull(form.investorPurchaseDate),
      loan_service_transfer_date: dateOrNull(form.loanServiceTransferDate),

      // revenue
      expected_gain: decimalStringOrNull(form.expectedGain),
      actual_gain: decimalStringOrNull(form.actualGain),
    };

    APIClient.post(`/uw/loan_applications/${app.id}/investor_detail`, formData)
      .then(resp => {
        const { investor_detail: investorDetail } = resp as { investor_detail: InvestorDetail };
        setLoadedInvestorData(investorDetail);
      })
      .catch(() => {
        alert("There was an error saving. Please contact Eng.");
      });
  };

  const renderRevenueForm = () => (
    <Form>
      <Select
        id="investorName"
        name="investorName"
        value={form.investorName}
        labelText="Investor"
        onChange={handleInputChange}
      >
        <SelectItem value="" text="Select investor..." />
        {enumKeys(InvestorNames).map(key => (
          <SelectItem text={InvestorNames[key]} value={key} key={key} />
        ))}
      </Select>
      <br />
      {form?.investorName === InvestorNames.MaxEx && (
        <div style={{ paddingLeft: "2rem" }}>
          <Select
            id="subInvestor"
            name="subInvestor"
            value={form.subInvestor}
            labelText="Sub-Investor"
            onChange={handleInputChange}
          >
            <SelectItem value="" text="Select sub-investor..." />
            {MaxExSubInvestors.map(subInvestorKey => (
              <SelectItem
                text={`${subInvestorKey} - ${MaxExSubInvestorNameMap[subInvestorKey]}`}
                value={subInvestorKey}
                key={subInvestorKey}
              />
            ))}
          </Select>
          <br />
        </div>
      )}
      <TextInput
        id="investorLoanNumber"
        name="investorLoanNumber"
        labelText="Investor loan number"
        value={form && form.investorLoanNumber}
        label="Investor loan number"
        style={{ maxWidth: "200px" }}
        onChange={handleInputChange}
      />
      <br />
      <Select
        id="warehouseLine"
        name="warehouseLine"
        value={form.warehouseLine}
        labelText="Warehouse Line"
        onChange={handleInputChange}
      >
        <SelectItem value="" text="Select warehouse line" />
        {WarehouseLineOptions.map(({ value, label }) => (
          <SelectItem text={label} value={value} key={value} />
        ))}
      </Select>
      <br />
      <DatePicker
        datePickerType="single"
        dateFormat="m/d/Y"
        value={form && form.investorRateLockDate}
        onChange={eventOrDates => {
          const date = eventOrDates.target ? eventOrDates.target.value : eventOrDates[0];
          setForm({ ...form, investorRateLockDate: date });
        }}
      >
        <DatePickerInput
          id="investorRateLockDate"
          name="investorRateLockDate"
          placeholder="mm/dd/yyyy"
          labelText="Investor rate lock date"
        />
      </DatePicker>
      <br />
      <DatePicker
        datePickerType="single"
        dateFormat="m/d/Y"
        value={form && form.investorRateLockExpirationDate}
        onChange={eventOrDates => {
          const date = eventOrDates.target ? eventOrDates.target.value : eventOrDates[0];
          setForm({ ...form, investorRateLockExpirationDate: date });
        }}
      >
        <DatePickerInput
          id="investorRateLockExpirationDate"
          name="investorRateLockExpirationDate"
          placeholder="mm/dd/yyyy"
          labelText="Investor rate lock expiration date"
        />
      </DatePicker>
      <br />
      <DatePicker
        datePickerType="single"
        dateFormat="m/d/Y"
        value={form && form.investorPurchaseDate}
        onChange={eventOrDates => {
          const date = eventOrDates.target ? eventOrDates.target.value : eventOrDates[0];
          setForm({ ...form, investorPurchaseDate: date });
        }}
      >
        <DatePickerInput
          id="investorPurchaseDate"
          name="investorPurchaseDate"
          placeholder="mm/dd/yyyy"
          labelText="Investor purchase date"
        />
      </DatePicker>
      <br />
      <DatePicker
        datePickerType="single"
        dateFormat="m/d/Y"
        value={form && form.loanServiceTransferDate}
        onChange={eventOrDates => {
          const date = eventOrDates.target ? eventOrDates.target.value : eventOrDates[0];
          setForm({ ...form, loanServiceTransferDate: date });
        }}
      >
        <DatePickerInput
          id="loanServiceTransferDate"
          name="loanServiceTransferDate"
          placeholder="mm/dd/yyyy"
          labelText="Loan service transfer date"
        />
      </DatePicker>
      <br />
      <TextInput
        id="expectedGain"
        name="expectedGain"
        value={form.expectedGain}
        labelText="Expected gain"
        placeholder="100.000"
        style={{ maxWidth: "200px" }}
        onChange={handleInputChange}
      />
      <div style={{ marginTop: "6px" }}>
        <p>
          Expected revenue:
          {form.expectedGain && (
            <span className={styles.revenue}>
              {formatCurrency((loanAmount * (form.expectedGain - 100)) / 100)}
            </span>
          )}
        </p>
        <br />
      </div>
      <br />
      <TextInput
        id="actualGain"
        name="actualGain"
        value={form.actualGain}
        labelText="Actual gain"
        placeholder="100.000"
        style={{ maxWidth: "200px" }}
        onChange={handleInputChange}
      />
      <div style={{ marginTop: "6px" }}>
        <p>
          Actual revenue:
          {form.actualGain && (
            <>
              <span className={styles.revenue}>
                {formatCurrency((loanAmount * (form.actualGain - 100)) / 100)}
              </span>
            </>
          )}
        </p>
        <br />
      </div>
      <TextArea
        id="investorProgramDescription"
        name="investorProgramDescription"
        value={form.investorProgramDescription}
        labelText="Investor program description"
        placeholder="description of investor program"
        onChange={handleInputChange}
      />
      <div className={styles.btnContainer}>
        <Button kind="primary" type="submit" onClick={submitRevenueForm} className={styles.ctaBtn}>
          Update
        </Button>
        <Button kind="secondary" onClick={resetForm} className={styles.ctaBtn}>
          Reset
        </Button>
      </div>
    </Form>
  );

  return (
    <HurdleView app={app} hurdle={hurdle} onHide={onHide}>
      MersNumber: {mers || "-"}
      <br />
      <br />
      <Button
        kind="primary"
        onClick={() => {
          APIClient.post(`/uw/${app.id}/fnma_32`, {})
            .then((response: any) => {
              const { filename, s3_key } = response;
              window.location.href = getFileDownloadBaseURL(s3_key, filename);
            })
            .catch(err => {
              logException(err);
              alert(
                `There was an error downloading the FNMA 3.2 data file. Please try again or notify #engineering.`,
              );
            });
        }}
      >
        Download FNMA 3.2 File
      </Button>
      <br />
      <br />
      {form ? getWarehouseLineButton(app.id, form.warehouseLine) : null}
      <br />
      <br />
      <div style={{ width: "50%" }}>{form ? renderRevenueForm() : <p>Loading... </p>}</div>
    </HurdleView>
  );
};

export default InvestorRateLockHurdle;
